import { Spinner } from '../Spinner';
import { cn } from '../../../lib/cn';

export const Button = ({ onClick, variant, color, size, rounded = 'lg', disabled, loading, children, className, icon: Icon, iconSize = 16, type = 'button' }) => {
  const isDisabled = disabled || loading;

  // Variant decides structure only; palette comes from the color map below.
  const buttonVariant = {
    filled: '',
    outlined: 'border bg-transparent',
    transparent: 'bg-transparent',
    default: '',
  };

  const buttonSize = {
    sm: 'min-h-[32px] px-3 text-xs',
    md: 'min-h-[40px] px-4 text-sm',
    lg: 'min-h-[48px] px-6 text-sm',
  };

  const buttonRounded = {
    none: 'rounded-none',
    sm: 'rounded-sm',
    md: 'rounded-md',
    lg: 'rounded-lg',
    xl: 'rounded-xl',
    full: 'rounded-full',
  };

  // Color decides palette only (bg/border/text + hover + disabled shades).
  const getButtonColor = () => {
    if (variant === 'transparent') {
      const transparentColors = {
        primary: isDisabled ? 'text-primary/50' : 'text-primary hover:text-primary/80',
        success: isDisabled ? 'text-success/50' : 'text-success hover:text-success/80',
        secondary: isDisabled ? 'text-secondary/50' : 'text-secondary-dark hover:text-secondary/80',
        danger: isDisabled ? 'text-danger/50' : 'text-danger hover:text-danger/80',
        white: isDisabled ? 'text-white/50' : 'text-white hover:text-white/80',
        default: isDisabled ? 'text-darkgrey/50' : 'text-darkgrey hover:text-darkgrey/80',
      };
      return transparentColors[color] || transparentColors.default;
    }

    const outlined = variant === 'outlined';
    const buttonColor = {
      primary: isDisabled
        ? (outlined ? 'text-primary/50 border-primary/50' : 'bg-primary/50 text-white')
        : (outlined ? 'text-primary border-primary hover:bg-primary/10' : 'bg-primary text-white hover:bg-primary/90'),
      success: isDisabled
        ? (outlined ? 'text-success/50 border-success/50' : 'bg-success/50 text-white')
        : (outlined ? 'text-success border-success hover:bg-success/10' : 'bg-success text-white hover:bg-success/80'),
      secondary: isDisabled
        ? (outlined ? 'text-secondary/50 border-secondary/50' : 'bg-secondary/50 text-primary')
        : (outlined ? 'text-secondary-dark border-secondary hover:bg-secondary/10' : 'bg-secondary text-primary hover:bg-secondary/90'),
      danger: isDisabled
        ? (outlined ? 'text-danger/60 border-danger/60' : 'bg-danger/50 text-white')
        : (outlined ? 'text-danger border-danger hover:bg-danger/10' : 'bg-danger text-white hover:bg-danger/85'),
      white: isDisabled
        ? (outlined ? 'text-white/50 border-white/30' : 'bg-white/50 text-primary')
        : (outlined ? 'text-white border-white/30 hover:bg-white/10' : 'bg-white text-primary hover:bg-white/90'),
      default: isDisabled
        ? (outlined ? 'text-primary/50 border-primary/50' : 'bg-primary/50 text-white')
        : (outlined ? 'text-primary border-primary hover:bg-primary/10' : 'bg-primary text-white hover:bg-primary/90'),
    };
    return buttonColor[color] || buttonColor.default;
  };

  const spinnerColor = variant === 'outlined' || variant === 'transparent'
    ? 'text-secondary-dark'
    : color === 'secondary'
      ? 'text-primary'
      : 'text-white';

  const buttonClasses = cn(
    'inline-flex items-center justify-center gap-1.5 min-w-12 font-medium tracking-widest select-none whitespace-nowrap transition',
    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-secondary/60 focus-visible:ring-offset-2',
    isDisabled ? 'cursor-not-allowed' : 'cursor-pointer',
    isDisabled && (variant === 'outlined' || variant === 'transparent') && 'opacity-50',
    rounded !== '' && rounded !== false && (buttonRounded[rounded] || buttonRounded.lg),
    buttonSize[size] || buttonSize.lg,
    buttonVariant[variant] || buttonVariant.default,
    getButtonColor(),
    className,
  );

  return (
    <button
      type={type}
      className={buttonClasses}
      onClick={isDisabled ? null : onClick}
      disabled={isDisabled}
      aria-busy={loading || undefined}
    >
      {loading ? (
        <Spinner className={spinnerColor} />
      ) : (
        <>
          {Icon && (
            <span className={cn('flex items-center justify-center shrink-0', isDisabled && 'opacity-50')}>
              <Icon width={iconSize} height={iconSize} />
            </span>
          )}
          {children}
        </>
      )}
    </button>
  );
};
